import { Popover } from '@chakra-ui/react'
import styled from '@emotion/styled'
import { useEffect, useState } from 'react'
import { EnumerationValue, EnumerationValues } from 'stylewhere/api'
import { Box, Icons, Input } from 'stylewhere/components'
// import { api } from 'stylewhere/api'
import { T, __ } from 'stylewhere/i18n'
import { TextFormSchemaField } from 'stylewhere/shared'
import { useDebouncedCallback } from 'use-debounce'

interface Props<Value = any> {
  disabled?: boolean
  index: number
  value: Value
  defaultValue: Value
  onChange: (value: any) => void
  field: TextFormSchemaField
  onModal?: boolean
}

export const CreateParcelGatePackagingCodeField: React.FC<Props> = ({
  index,
  field,
  defaultValue,
  value,
  onChange,
  disabled,
  onModal
}) => {
  const [popoverData, setPopoverData] = useState<{
    width: string
    height: string
    depth: string
    tare: string
    description: string
  }>()
  const Icon = field.icon && Icons[field.icon]
  const debouncedFetch = useDebouncedCallback(
    async () => {
      const result = await EnumerationValues.search<EnumerationValue>({
        enumerationTypeCode: 'PACKAGING',
        equalCodes: value
      })
      if (result.content.length > 0) {
        const packaging = result.content[0]
        const { width, height, depth, tare } = packaging.attributes
        setPopoverData(packaging ? { width, height, depth, tare, description: packaging.description } : undefined)
      }
    },
    400,
    { leading: true }
  )

  useEffect(() => {
    setPopoverData(undefined)
    if (value) {
      debouncedFetch()
    }
  }, [value, debouncedFetch])

  return (
    <Container style={{ marginBottom: onModal ? 80 : 0 }}>
      <Input
        key={`input${index.toString()}`}
        defaultValue={defaultValue}
        required={field.required ?? false}
        onChange={onChange}
        value={value}
        id={field.name}
        type={field.type}
        placeholder={field.secondaryLabel}
        startFocus={field.focus}
        image={Icon && <Icon />}
        label={field.required ? `${field.label} *` : field.label}
        disabled={field.disabled ?? disabled}
        minLength={field.minlength}
        maxLength={field.maxlength}
        debounce={200}
      />

      <Popover.Root
        lazyMount
        open={!!popoverData}
        positioning={{
          placement: onModal ? 'bottom' : 'right',
          offset: onModal ? { mainAxis: 0, crossAxis: 60 } : { mainAxis: 57, crossAxis: 30 },
          hideWhenDetached: true
        }}
      >
        <Popover.Trigger asChild>
          <StyledTrigger>&nbsp;</StyledTrigger>
        </Popover.Trigger>
        <Popover.Positioner>
          <Popover.Content borderWidth={0} borderRadius={10} width="100%" boxShadow="base">
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <Popover.Body>
              <Box flex row>
                <div style={{ flex: '50%' }}>
                  {__(T.create_parcel_gate.width)}: <strong>{popoverData?.width}</strong>
                </div>
                <div style={{ flex: '50%' }}>
                  {__(T.create_parcel_gate.height)}: <strong>{popoverData?.height}</strong>
                </div>
              </Box>
              <Box flex row>
                <div style={{ flex: '50%' }}>
                  {__(T.create_parcel_gate.depth)}: <strong>{popoverData?.depth}</strong>
                </div>
                <div style={{ flex: '50%' }}>
                  {__(T.create_parcel_gate.tare)}: <strong>{popoverData?.tare}</strong>
                </div>
              </Box>
              <div style={{ marginTop: 10 }}>
                {__(T.create_parcel_gate.description)}: <strong>{popoverData?.description}</strong>
              </div>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
`

const StyledTrigger = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
`
